import { useEffect, useState } from "react";

interface ScoreGaugeProps {
  score: number;
  verdict: "LIKELY_REAL" | "POSSIBLY_GHOST" | "LIKELY_GHOST";
}

const VERDICT_COLORS = {
  LIKELY_REAL: "#22c55e",
  POSSIBLY_GHOST: "#f59e0b",
  LIKELY_GHOST: "#ff4444",
};

export function ScoreGauge({ score, verdict }: ScoreGaugeProps) {
  const [animatedScore, setAnimatedScore] = useState(0);

  const radius = 76;
  const strokeWidth = 6;
  const viewBox = 192;
  const circumference = 2 * Math.PI * radius;
  const color = VERDICT_COLORS[verdict];

  useEffect(() => {
    const start = Date.now();
    const duration = 1600;

    function tick() {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(eased * score));
      if (progress < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }, [score]);

  const offset = circumference - (animatedScore / 100) * circumference;
  const center = viewBox / 2;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[min(192px,48vw)] aspect-square">
        <svg
          viewBox={`0 0 ${viewBox} ${viewBox}`}
          className="w-full h-full"
          aria-label={`Score: ${score} out of 100`}
        >
          {/* Subtle tick marks */}
          {Array.from({ length: 40 }).map((_, i) => {
            const angle = (i / 40) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const r1 = radius + 12;
            const r2 = radius + (i % 5 === 0 ? 16 : 14);
            return (
              <line
                key={i}
                x1={center + r1 * Math.cos(rad)}
                y1={center + r1 * Math.sin(rad)}
                x2={center + r2 * Math.cos(rad)}
                y2={center + r2 * Math.sin(rad)}
                stroke="rgba(255,255,255,0.04)"
                strokeWidth={i % 5 === 0 ? 1.5 : 0.5}
              />
            );
          })}

          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth={strokeWidth}
          />

          {/* Progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{
              filter: `drop-shadow(0 0 12px ${color}66)`,
              transition: "stroke-dashoffset 0.05s linear",
            }}
          />
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="text-4xl sm:text-5xl font-normal text-white leading-none"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {animatedScore}
          </span>
          <span
            className="mt-0.5 sm:mt-1"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.55rem",
              color: "var(--text-tertiary)",
              letterSpacing: "0.1em",
            }}
          >
            / 100
          </span>
        </div>
      </div>
    </div>
  );
}
